import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaStar, FaClock, FaShoppingCart, FaCheck, FaBolt } from 'react-icons/fa';
import API from '../api/axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ServiceDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const res = await API.get(`/services/${id}`);
        setService(res.data.service);
        setReviews(res.data.reviews || []);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchService();
  }, [id]);

  const handleOrder = () => {
    if (!user) return navigate('/login');
    navigate(`/checkout/${id}`, { state: { packageIndex: selectedPackage } });
  };

  if (loading) return (<><Navbar /><div style={{ paddingTop: '120px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div></>);
  if (!service) return (<><Navbar /><div style={{ paddingTop: '120px', textAlign: 'center', color: 'var(--text-muted)' }}>Service not found</div></>);

  const currentPkg = service.packages?.length > 0 ? service.packages[selectedPackage] : null;
  const price = currentPkg ? currentPkg.price : (service.discountPrice || service.price);
  const deliveryDays = currentPkg ? currentPkg.deliveryDays : service.deliveryTime;

  return (
    <>
      <Navbar />
      <div style={{ paddingTop: '100px', minHeight: '100vh' }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
              {/* Left - Service Info */}
              <div style={{ flex: '2', minWidth: '300px' }}>
                <h1 style={{ fontSize: '2rem', marginBottom: '10px' }}>{service.title}</h1>
                <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', color: 'var(--text-muted)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FaStar style={{ color: '#ffc107' }} /> {service.rating} ({service.totalReviews} reviews)</span>
                  <span><FaShoppingCart /> {service.totalOrders} orders</span>
                </div>
                <div style={{ background: 'var(--bg-card)', borderRadius: '20px', padding: '30px', border: '1px solid var(--border)', marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px', color: 'var(--primary)' }}>Description</h3>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>{service.description}</p>
                </div>

                {service.features?.length > 0 && (
                  <div style={{ background: 'var(--bg-card)', borderRadius: '20px', padding: '30px', border: '1px solid var(--border)', marginBottom: '30px' }}>
                    <h3 style={{ marginBottom: '15px', color: 'var(--primary)' }}>What's Included</h3>
                    {service.features.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', color: 'var(--text-secondary)' }}>
                        <FaCheck style={{ color: '#00d2ff' }} /> {f}
                      </div>
                    ))}
                  </div>
                )}

                {/* Reviews */}
                <div style={{ background: 'var(--bg-card)', borderRadius: '20px', padding: '30px', border: '1px solid var(--border)' }}>
                  <h3 style={{ marginBottom: '20px', color: 'var(--primary)' }}>Reviews ({reviews.length})</h3>
                  {reviews.length === 0 && <p style={{ color: 'var(--text-muted)' }}>No reviews yet</p>}
                  {reviews.map((review, i) => (
                    <div key={i} style={{ borderBottom: '1px solid var(--border)', paddingBottom: '15px', marginBottom: '15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <strong>{review.client?.name || 'Anonymous'}</strong>
                        <div style={{ color: '#ffc107' }}>{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</div>
                      </div>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{review.comment}</p>
                      {review.adminReply && (
                        <div style={{ marginTop: '10px', paddingLeft: '15px', borderLeft: '2px solid var(--primary)', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                          <strong>Reply:</strong> {review.adminReply}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right - Order Box */}
              <div style={{ flex: '1', minWidth: '300px' }}>
                <div style={{ background: 'var(--bg-card)', borderRadius: '20px', padding: '30px', border: '1px solid var(--border)', position: 'sticky', top: '100px' }}>
                  {/* Package Tabs */}
                  {service.packages?.length > 0 && (
                    <div style={{ display: 'flex', gap: '5px', marginBottom: '20px' }}>
                      {service.packages.map((pkg, i) => (
                        <button key={i} onClick={() => setSelectedPackage(i)}
                          style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins', fontWeight: '600',
                            background: selectedPackage === i ? 'var(--gradient-1)' : 'rgba(108,99,255,0.1)', color: selectedPackage === i ? 'white' : 'var(--text-secondary)' }}>
                          {pkg.name}
                        </button>
                      ))}
                    </div>
                  )}

                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <span style={{ fontSize: '2.5rem', fontWeight: '800', background: 'var(--gradient-1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>${price}</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', color: 'var(--text-secondary)' }}>
                    <span><FaClock /> Delivery Time</span>
                    <strong>{deliveryDays} days</strong>
                  </div>

                  {currentPkg?.features?.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      {currentPkg.features.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                          <FaCheck style={{ color: '#00d2ff', fontSize: '0.75rem' }} /> {f}
                        </div>
                      ))}
                    </div>
                  )}

                  {service.rushDelivery?.available && (
                    <div style={{ padding: '12px', background: 'rgba(255,101,132,0.1)', borderRadius: '10px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'var(--secondary)' }}>
                      <FaBolt /> Rush delivery in {service.rushDelivery.deliveryDays} day (+${service.rushDelivery.extraPrice})
                    </div>
                  )}

                  <button onClick={handleOrder} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '15px', fontSize: '1.1rem' }}>
                    <FaShoppingCart /> Order Now
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ServiceDetail;
